// components/artifacts/charts/PolarAreaChartComponent.tsx
import React from "react";
import { 
  RadialBarChart, 
  RadialBar, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Cell
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { GraphData } from "@/types/Artifact";
import { formatNumber } from "@/lib/utils";

interface PolarAreaChartComponentProps {
  data: GraphData;
  config: Record<string, any>;
  colors: string[];
  id?: string;
}

// Custom tooltip formatter that includes units
const formatTooltipValue = (value: any, unit?: string) => {
  let y = value
  //if value is number, convert to string
  if (typeof value === 'number') {
    y = formatNumber(y);
  } 

  if (!unit) return value;
  
  // Handle different unit types
  switch (unit) {
    case '$':
    case '€':
    case '£':
    case '¥':
      return `${unit}${y}`;
    case '%':
      return `${y}${unit}`;
    default:
      return `${y} ${unit}`;
  }
};

export const PolarAreaChartComponent: React.FC<PolarAreaChartComponentProps> = React.memo(({
  data,
  config,
  colors,
  id,
}) => {
  const categoryKey = data.categoryKey || "category";
  const valueKey = data.valueKey || "value";
  const { valueUnit } = data;

  return (
    <div className="p-4" id={id}>
      {/* Display unit information if available */}
      {/* {valueUnit && (
        <div className="mb-2 text-sm text-muted-foreground">
          Values: {valueUnit}
        </div>
      )} */}
      
      <ChartContainer
        config={config}
        className="max-h-[calc(100vh-10rem)] min-h-[400px] w-full"
      >
        <RadialBarChart
          accessibilityLayer
          cx="50%"
          cy="50%"
          innerRadius="10%"
          outerRadius="80%"
          barSize={20}
          data={data.data}
        >
          <PolarAngleAxis
            type="category"
            dataKey={categoryKey}
            tick={{ fill: "#888", fontSize: 14 }}
          />
          <PolarRadiusAxis 
            angle={90} 
            domain={[0, "auto"]} 
            tick={{ fill: "#888", fontSize: 12 }}
            tickFormatter={(value) => formatTooltipValue(value, valueUnit)}
          />
          <RadialBar 
            background 
            dataKey={valueKey} 
            cornerRadius={8}
            fill="#8884d8"
          >
            {data.data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </RadialBar>
          <ChartLegend 
            content={<ChartLegendContent />}
            iconSize={10} 
            layout="vertical" 
            verticalAlign="middle" 
            align="right" 
          />
          <ChartTooltip 
            content={<ChartTooltipContent 
              valueFormatter={(value) => formatTooltipValue(value, valueUnit)}
            />} 
          />
        </RadialBarChart>
      </ChartContainer>
    </div>
  );
});